import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errText } from "../../lib/api";
import { MOROCCO } from "../../lib/countries";
import Seo, { JsonLd } from "../../components/Seo";
import { trackPartnerSignup } from "../../components/MarketingLoader";
import {
  ArrowRight, Wallet, Clock, MapPin, ShieldCheck, ThumbsUp, Star,
  ChevronDown, Check, Bike, Car, Truck as TruckIcon, CheckCircle2, Loader2, Upload,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1526367790999-0150786686a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CITY_IMG = "https://images.unsplash.com/photo-1518544801976-3e159e50e5bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

// Latin-only helper (mirrors the florist form — downstream bank/tax systems require Latin).
const latinOnly = (s) => (s || "").replace(/[^A-Za-z0-9 .,'’\-@&/()#+\n]/g, "");

const SEO_META = {
  fr: {
    title: "Devenir livreur Fleurinelle · Gagnez avec vos livraisons partout au Maroc",
    description: "Rejoignez le réseau de livreurs Fleurinelle. Rémunération par zone, règlement hebdomadaire en dirhams, choix des zones et des jours. Candidature en 5 minutes.",
    keywords: "livreur maroc, job livreur fleurs maroc, delivery job casablanca marrakech, gagner argent scooter maroc, livreur partenaire fleurinelle, travail livreur agadir tanger rabat",
  },
  en: {
    title: "Become a Fleurinelle Driver · Earn on every delivery across Morocco",
    description: "Join the Fleurinelle driver network. Per-zone pay, weekly payouts in dirhams, pick your zones and days. Apply in 5 minutes, live in 24 hours.",
    keywords: "delivery driver morocco, courier job casablanca, scooter delivery job marrakech, earn money delivery morocco, fleurinelle driver, part time driver rabat tangier agadir",
  },
  es: {
    title: "Sea repartidor Fleurinelle · Gane con sus entregas en todo Marruecos",
    description: "Únase a la red de repartidores Fleurinelle. Pago por zona, ingresos semanales en dirhams, elija sus zonas y días. Candidatura en 5 minutos.",
    keywords: "repartidor marruecos, trabajo repartidor casablanca, entrega flores marruecos, ganar dinero scooter marruecos, repartidor fleurinelle",
  },
  ar: {
    title: "كن سائق توصيل Fleurinelle · اربح من كلّ توصيلة في المغرب",
    description: "انضمّ إلى شبكة سائقي Fleurinelle. أجر حسب المنطقة، تسديد أسبوعي بالدرهم، اختر مناطقك وأيّامك. طلب في 5 دقائق.",
    keywords: "سائق توصيل المغرب، عمل سائق توصيل ورود، الدار البيضاء مراكش، ربح مع سكوتر المغرب",
  },
};

const COPY = {
  fr: {
    dir: "ltr",
    hero: {
      eyebrow: "Livreurs Fleurinelle · Maroc",
      headA: "Vos livraisons méritent",
      headB: "un vrai revenu.",
      body: "Rejoignez la flotte de livreurs Fleurinelle. Vous choisissez vos zones et vos jours. La rémunération est fixe par livraison — sans surprise, en dirhams, chaque lundi.",
      cta: "Commencer ma candidature",
      loginLink: "Je suis déjà livreur",
      badgeFree: "Sans frais d'inscription",
      badgeFlex: "Vos horaires, vos zones",
      badgeFast: "Actif en 24 h",
      floatingKicker: "Ce mois-ci",
      floatingSub: "de gains moyens\npour les livreurs actifs 5 j",
    },
    stats: [
      { value: "20 MAD", label: "Rémunération moyenne par livraison réussie" },
      { value: "Lundi", label: "Versement hebdomadaire sur votre RIB" },
      { value: "24 h", label: "Activation après validation du dossier" },
      { value: "0 MAD", label: "Aucun frais d'inscription, jamais" },
    ],
    benefits: {
      eyebrow: "Pourquoi rouler pour Fleurinelle",
      title: "Un revenu clair,",
      titleB: "avec vraiment moins d'attente.",
      cards: [
        { icon: Wallet, title: "Rémunération par zone", body: "Chaque zone a un tarif fixe et affiché. Vous savez à l'avance combien rapporte une course avant de l'accepter." },
        { icon: Clock, title: "Règlement chaque lundi", body: "Virement automatique en dirhams sur votre RIB. Relevé PDF détaillé à chaque semaine." },
        { icon: MapPin, title: "Vous choisissez vos zones", body: "Cochez uniquement les zones que vous souhaitez couvrir. Vous pouvez les modifier à tout moment depuis l'application." },
        { icon: ShieldCheck, title: "Assurance course incluse", body: "Fleurinelle prend en charge l'assurance pour chaque bouquet transporté, plus une assistance 24 / 24 si un imprévu survient." },
        { icon: ThumbsUp, title: "Application simple", body: "Une seule vue : la commande à récupérer, l'adresse client, la carte GPS et le bouton de preuve photo. Aucun dispatcher à appeler." },
        { icon: Bike, title: "Votre véhicule, vos règles", body: "Scooter, voiture ou fourgonette : nous acceptons tous les véhicules assurés et en règle." },
      ],
    },
    how: {
      eyebrow: "Comment ça marche",
      title: "Actif la même semaine.",
      steps: [
        { t: "Candidature en 5 minutes", d: "Formulaire unique — vos coordonnées, votre véhicule, quelques documents (CIN, permis, casier judiciaire)." },
        { t: "Vérification sous 24 h", d: "Notre équipe valide vos documents et vos zones. Vous suivez chaque étape depuis l'application livreur." },
        { t: "Première course, puis actif", d: "Une course test confirme votre parcours. Vos premières livraisons rémunérées arrivent la même semaine." },
      ],
    },
    insight: {
      eyebrow: "Un vrai complément de revenu",
      titleA: "Livrez quand ça vous arrange,",
      titleB: "gagnez régulièrement.",
      body: "Fleurinelle génère des commandes toute la semaine — pics le vendredi, samedi, dimanche et à chaque fête (Saint-Valentin, fête des mères, Aïd). Les livreurs actifs cinq jours par semaine dépassent régulièrement 2 000 MAD par mois.",
      points: [
        ["+22 %", "de commandes le samedi par rapport à un jour de semaine ordinaire."],
        ["18 min", "temps moyen entre l'acceptation et la remise au client."],
        ["93 %", "des courses sont réalisées sans le moindre incident déclaré."],
        ["3 pics", "de forte activité par an (Saint-Valentin, fête des mères, Aïd)."],
      ],
      panelKicker: "Où on livre",
      panelTitle: "Casablanca, Marrakech, Rabat, Tanger, Agadir — et nous ajoutons de nouvelles villes chaque trimestre.",
      panelBody: "Nous n'ouvrons une ville que lorsque plusieurs boutiques y sont partenaires — la demande est là dès votre premier jour actif.",
    },
    testimonials: {
      eyebrow: "Ce que disent les livreurs",
      title: "Des livreurs, comme vous.",
      items: [
        { quote: "Je fais 30 courses par semaine en scooter. Chaque lundi je reçois mon virement, exactement le montant annoncé.", name: "Youssef E.", role: "Scooter · Casablanca", initials: "YE" },
        { quote: "L'application est simple, on voit directement l'adresse, on prend la photo, c'est fini. Aucun temps perdu au dispatch.", name: "Karim T.", role: "Voiture · Marrakech", initials: "KT" },
      ],
    },
    faq: {
      eyebrow: "Questions fréquentes",
      title: "On répond avant que vous demandiez.",
      contactPrefix: "Une question ? Écrivez à",
      items: [
        { q: "Quels véhicules acceptez-vous ?", a: "Scooter, voiture ou fourgonette — tant qu'ils sont assurés, en règle, et propres. Votre véhicule reste le vôtre, Fleurinelle ne prend rien dessus." },
        { q: "Combien puis-je gagner ?", a: "La rémunération dépend de la zone livrée et du nombre de courses. En moyenne 15 à 30 MAD par livraison. Nos livreurs actifs 5 jours par semaine gagnent 1 500 à 2 500 MAD par mois." },
        { q: "Quels documents sont nécessaires ?", a: "CIN, permis de conduire, carte grise, attestation d'assurance et un extrait de casier judiciaire de moins de 3 mois." },
        { q: "Puis-je livrer uniquement le week-end ?", a: "Oui. Vous choisissez vos horaires et vos zones. Vous pouvez tout modifier depuis votre espace, en un clic." },
        { q: "Comment sont calculés les paiements ?", a: "Chaque zone a un tarif fixe affiché avant que vous acceptiez la course. Le paiement complet vous est versé le lundi suivant sur votre RIB, avec relevé PDF." },
      ],
    },
    final: { title: "Prêt à rouler pour Fleurinelle ?", body: "Moins de 5 minutes pour candidater. Notre équipe revient vers vous sous 24 h ouvrées.", cta: "Commencer ma candidature" },
    form: {
      title: "Candidature livreur Fleurinelle",
      intro: "Nous travaillons avec des livreurs indépendants à Casablanca, Marrakech, Rabat, Tanger et Agadir. Rémunération à la course, règlement chaque lundi.",
      latinNotice: "Toutes les informations doivent être saisies en caractères latins (a-z, 0-9).",
      section1: "1. Votre identité",
      name: "Nom complet", email: "E-mail", password: "Mot de passe (8+)", phone: "Téléphone", whatsapp: "WhatsApp",
      cin: "N° de CIN", licenseNumber: "N° permis de conduire",
      section2: "2. Votre véhicule",
      vehicleType: "Type de véhicule",
      typeScooter: "Scooter", typeCar: "Voiture", typeVan: "Fourgonette",
      brand: "Marque", model: "Modèle", year: "Année", plate: "Immatriculation",
      section3: "3. Où voulez-vous livrer ?",
      city: "Ville", zones: "Zones", zonesHint: "Sélectionnez toutes les zones que vous souhaitez couvrir.",
      section4: "4. Documents obligatoires",
      docsHint: "Chargez chaque document en JPG ou PDF (8 Mo max). Nos équipes les vérifient sous 24 h ouvrées.",
      docsAccept: "JPG ou PDF · 8 Mo max",
      docJpgOnly: "JPG · 8 Mo max",
      docUpload: "Charger", docChange: "Remplacer",
      docRecto: "Recto", docVerso: "Verso",
      docErrSize: "Fichier trop volumineux (8 Mo max).",
      docErrFormat: "Format non accepté. Chargez un JPG ou un PDF.",
      docErrJpg: "Ce document doit être une photo JPG.",
      criminal: "Extrait de casier judiciaire", criminalHint: "PDF ou photo. Moins de 3 mois.",
      idDoc: "CIN (recto ou recto-verso)", licenseDoc: "Permis de conduire",
      autoAttestation: "Attestation d'auto-entrepreneur",
      autoCard: "Carte d'auto-entrepreneur",
      carteGrise: "Carte grise",
      insurance: "Attestation d'assurance",
      vehicleBack: "Photo de la plaque arrière du véhicule",
      docPdfOrJpg: "PDF, JPG ou JPEG.",
      docOptional: "Optionnel — chargez plus tard depuis l'application.",
      submit: "Envoyer ma candidature", submitting: "Envoi…",
      consent: "J'ai lu et j'accepte le", agreement: "Contrat de livreur partenaire", consentEnd: ", en mon nom en tant qu'auto-entrepreneur indépendant.",
      chooseCity: "— Choisir —",
      submitHint: "Cela prend moins de 5 minutes. Nous répondons sous 24 h ouvrées.",
      success: "Candidature envoyée",
      successHint: "Notre équipe examine votre dossier. Vous recevrez un e-mail dès l'activation.",
      errPhone: "Numéro trop court.", errEmail: "Format d'e-mail invalide.",
      errPassword: "caractères minimum.", errName: "Nom trop court.",
      errPlate: "Immatriculation invalide.", errYear: "Année invalide.",
      brandPh: "ex. Honda", modelPh: "ex. PCX 125", yearPh: "ex. 2019", platePh: "12345-A-6",
      cinPh: "AB123456", licenseNumberPh: "M/123456",
    },
  },
  en: {
    dir: "ltr",
    hero: {
      eyebrow: "Fleurinelle Drivers · Morocco",
      headA: "Your deliveries deserve",
      headB: "real earnings.",
      body: "Join the Fleurinelle driver network. Pick your zones and your days. Pay is fixed per delivery — no surprises, in dirhams, every Monday.",
      cta: "Start my application",
      loginLink: "I'm already a driver",
      badgeFree: "No signup fee",
      badgeFlex: "Your hours, your zones",
      badgeFast: "Live in 24 h",
      floatingKicker: "This month",
      floatingSub: "average earnings\nfor drivers active 5 days",
    },
    stats: [
      { value: "20 MAD", label: "Average pay per successful delivery" },
      { value: "Monday", label: "Weekly payout straight to your RIB" },
      { value: "24 h", label: "Time to activation after review" },
      { value: "0 MAD", label: "No signup fee, ever" },
    ],
    benefits: {
      eyebrow: "Why drive for Fleurinelle",
      title: "Transparent pay,",
      titleB: "genuinely less waiting around.",
      cards: [
        { icon: Wallet, title: "Per-zone pay", body: "Every zone has a fixed, visible rate. You know what a ride pays before accepting it." },
        { icon: Clock, title: "Weekly payouts", body: "Automatic transfer to your RIB every Monday in dirhams. Detailed PDF statement each week." },
        { icon: MapPin, title: "You pick your zones", body: "Tick only the zones you want to cover. You can change them any time from the app." },
        { icon: ShieldCheck, title: "Ride insurance included", body: "Fleurinelle insures every bouquet in transit, plus 24 / 7 assistance if anything goes wrong." },
        { icon: ThumbsUp, title: "Simple app", body: "One view: pickup order, customer address, GPS map, proof-photo button. No dispatcher to call." },
        { icon: Bike, title: "Your vehicle, your rules", body: "Scooter, car or van — we accept every properly-insured, road-legal vehicle." },
      ],
    },
    how: {
      eyebrow: "How it works",
      title: "Live within a week.",
      steps: [
        { t: "Apply in 5 minutes", d: "One form — your details, your vehicle, a handful of documents (ID, licence, criminal record)." },
        { t: "Reviewed within 24 h", d: "Our team validates your documents and zones. You track every stage from the driver app." },
        { t: "Test ride, then live", d: "A test ride confirms your process. Your first paid deliveries arrive that same week." },
      ],
    },
    insight: {
      eyebrow: "A real side income",
      titleA: "Drive when it suits you,",
      titleB: "earn every week.",
      body: "Fleurinelle generates orders throughout the week — peaks on Friday, Saturday, Sunday and at every holiday (Valentine's, Mother's Day, Eid). Drivers active five days a week regularly clear 2 000 MAD a month.",
      points: [
        ["+22 %", "more orders on Saturday than on a regular weekday."],
        ["18 min", "average time between accepting a ride and handing to the customer."],
        ["93 %", "of rides run with zero reported incidents."],
        ["3 peaks", "of heavy activity per year (Valentine's, Mother's Day, Eid)."],
      ],
      panelKicker: "Where we deliver",
      panelTitle: "Casablanca, Marrakech, Rabat, Tangier, Agadir — and we open new cities every quarter.",
      panelBody: "We only open a city once several florists are on board — demand is there from your first active day.",
    },
    testimonials: {
      eyebrow: "What drivers say",
      title: "Drivers just like you.",
      items: [
        { quote: "I do 30 rides a week on my scooter. Every Monday I get my transfer, exactly the amount promised.", name: "Youssef E.", role: "Scooter · Casablanca", initials: "YE" },
        { quote: "The app is simple, I see the address, take the photo, done. No time lost with dispatch.", name: "Karim T.", role: "Car · Marrakech", initials: "KT" },
      ],
    },
    faq: {
      eyebrow: "Frequently asked",
      title: "Answered before you ask.",
      contactPrefix: "A question? Write to",
      items: [
        { q: "Which vehicles do you accept?", a: "Scooter, car or van — as long as they're insured, road-legal, and clean. Your vehicle stays yours, Fleurinelle takes nothing on it." },
        { q: "How much can I earn?", a: "Pay depends on the zone and the number of rides. Averages: 15 to 30 MAD per delivery. Drivers active 5 days a week earn 1 500 to 2 500 MAD a month." },
        { q: "What documents do I need?", a: "National ID, driver's licence, vehicle registration, insurance certificate, and a criminal-record extract less than 3 months old." },
        { q: "Can I only drive on weekends?", a: "Yes. You pick your hours and zones. All settings are one-click from your account." },
        { q: "How is my pay calculated?", a: "Every zone has a fixed rate displayed before you accept the ride. Full payment arrives on Monday on your RIB, with a PDF statement." },
      ],
    },
    final: { title: "Ready to ride for Fleurinelle?", body: "Under 5 minutes to apply. We get back to you within 24 business hours.", cta: "Start my application" },
    form: {
      title: "Fleurinelle Driver Application",
      intro: "We work with independent drivers in Casablanca, Marrakech, Rabat, Tangier and Agadir. Per-ride pay, weekly payouts.",
      latinNotice: "All fields must be entered in Latin characters (a-z, 0-9).",
      section1: "1. Your details",
      name: "Full name", email: "Email", password: "Password (8+)", phone: "Phone", whatsapp: "WhatsApp",
      cin: "National ID number", licenseNumber: "Driver's licence number",
      section2: "2. Your vehicle",
      vehicleType: "Vehicle type",
      typeScooter: "Scooter", typeCar: "Car", typeVan: "Van",
      brand: "Brand", model: "Model", year: "Year", plate: "License plate",
      section3: "3. Where do you want to deliver?",
      city: "City", zones: "Zones", zonesHint: "Select every zone you're willing to cover.",
      section4: "4. Mandatory documents",
      docsHint: "Upload each document as JPG or PDF (8 MB max). Our team reviews within 24 business hours.",
      docsAccept: "JPG or PDF · 8 MB max",
      docJpgOnly: "JPG · 8 MB max",
      docUpload: "Upload", docChange: "Replace",
      docRecto: "Front", docVerso: "Back",
      docErrSize: "File too large (8 MB max).",
      docErrFormat: "Format not accepted. Upload a JPG or a PDF.",
      docErrJpg: "This document must be a JPG photo.",
      criminal: "Criminal-record extract", criminalHint: "PDF or photo. Under 3 months old.",
      idDoc: "National ID (front or both sides)", licenseDoc: "Driver's licence",
      autoAttestation: "Autoentrepreneur attestation",
      autoCard: "Autoentrepreneur card",
      carteGrise: "Vehicle registration (Carte Grise)",
      insurance: "Insurance certificate",
      vehicleBack: "Rear vehicle plate photo",
      docPdfOrJpg: "PDF, JPG or JPEG.",
      docOptional: "Optional — upload later from the app.",
      submit: "Submit application", submitting: "Sending…",
      consent: "I have read and accept the", agreement: "Delivery Partner Agreement", consentEnd: ", on my own behalf as an independent autoentrepreneur.",
      chooseCity: "— Choose —",
      submitHint: "Under 5 minutes to apply. We get back to you within 24 business hours.",
      success: "Application submitted",
      successHint: "Our team is reviewing your file. You'll get an email as soon as you're activated.",
      errPhone: "Number too short.", errEmail: "Invalid email format.",
      errPassword: "characters minimum.", errName: "Name too short.",
      errPlate: "Invalid plate.", errYear: "Invalid year.",
      brandPh: "e.g. Honda", modelPh: "e.g. PCX 125", yearPh: "e.g. 2019", platePh: "12345-A-6",
      cinPh: "AB123456", licenseNumberPh: "M/123456",
    },
  },
  es: {
    dir: "ltr",
    hero: {
      eyebrow: "Repartidores Fleurinelle · Marruecos",
      headA: "Sus entregas merecen",
      headB: "ingresos de verdad.",
      body: "Únase a la red de repartidores Fleurinelle. Elija sus zonas y sus días. Pago fijo por entrega, sin sorpresas, en dirhams, cada lunes.",
      cta: "Iniciar mi candidatura", loginLink: "Ya soy repartidor",
      badgeFree: "Sin cuota de inscripción", badgeFlex: "Sus horarios, sus zonas", badgeFast: "Activo en 24 h",
      floatingKicker: "Este mes", floatingSub: "de ingresos medios\npara repartidores 5 días",
    },
    stats: [
      { value: "20 MAD", label: "Pago medio por entrega realizada" },
      { value: "Lunes", label: "Ingreso semanal directo a su RIB" },
      { value: "24 h", label: "Tiempo hasta la activación tras revisión" },
      { value: "0 MAD", label: "Sin cuota de inscripción, nunca" },
    ],
    benefits: {
      eyebrow: "Por qué repartir con Fleurinelle",
      title: "Pago transparente,",
      titleB: "de verdad menos espera.",
      cards: [
        { icon: Wallet, title: "Pago por zona", body: "Cada zona tiene una tarifa fija y visible. Sabe cuánto paga un viaje antes de aceptarlo." },
        { icon: Clock, title: "Pagos semanales", body: "Transferencia automática cada lunes a su RIB, en dirhams. Recibo PDF detallado cada semana." },
        { icon: MapPin, title: "Elige sus zonas", body: "Marque solo las zonas que quiere cubrir. Puede cambiarlas en cualquier momento desde la app." },
        { icon: ShieldCheck, title: "Seguro incluido", body: "Fleurinelle asegura cada ramo en tránsito, más asistencia 24 / 7 ante imprevistos." },
        { icon: ThumbsUp, title: "App sencilla", body: "Una vista: recogida, dirección, GPS, botón foto. Sin dispatcher al que llamar." },
        { icon: Bike, title: "Su vehículo, sus reglas", body: "Scooter, coche o furgoneta: aceptamos cualquier vehículo asegurado y en regla." },
      ],
    },
    how: {
      eyebrow: "Cómo funciona", title: "Activo la misma semana.",
      steps: [
        { t: "Candidatura en 5 minutos", d: "Un formulario — sus datos, su vehículo, algunos documentos (DNI, permiso, antecedentes)." },
        { t: "Revisión en 24 h", d: "Nuestro equipo valida documentos y zonas. Sigue cada etapa desde la app de repartidor." },
        { t: "Viaje de prueba, luego activo", d: "Un viaje test confirma su proceso. Los primeros viajes pagados llegan esa misma semana." },
      ],
    },
    insight: {
      eyebrow: "Un ingreso complementario real",
      titleA: "Reparta cuando le convenga,", titleB: "gane cada semana.",
      body: "Fleurinelle genera pedidos toda la semana — picos viernes, sábado, domingo y cada fiesta. Repartidores 5 días/semana superan 2 000 MAD al mes.",
      points: [
        ["+22 %", "más pedidos el sábado que un día laborable."],
        ["18 min", "tiempo medio entre aceptar y entregar."],
        ["93 %", "de viajes sin ninguna incidencia."],
        ["3 picos", "de gran actividad al año (San Valentín, Día de la Madre, Eid)."],
      ],
      panelKicker: "Dónde repartimos",
      panelTitle: "Casablanca, Marrakech, Rabat, Tánger, Agadir — nuevas ciudades cada trimestre.",
      panelBody: "Solo abrimos una ciudad cuando varias floristerías son socias — la demanda está desde el primer día.",
    },
    testimonials: {
      eyebrow: "Lo que dicen los repartidores",
      title: "Repartidores como usted.",
      items: [
        { quote: "Hago 30 viajes semanales en scooter. Cada lunes recibo la transferencia, exactamente lo prometido.", name: "Youssef E.", role: "Scooter · Casablanca", initials: "YE" },
        { quote: "La app es simple, veo la dirección, saco la foto y listo. Sin perder tiempo con el dispatcher.", name: "Karim T.", role: "Coche · Marrakech", initials: "KT" },
      ],
    },
    faq: {
      eyebrow: "Preguntas frecuentes", title: "Respondemos antes de que pregunte.",
      contactPrefix: "¿Una pregunta? Escríbanos a",
      items: [
        { q: "¿Qué vehículos aceptan?", a: "Scooter, coche o furgoneta, siempre que estén asegurados, en regla y limpios. Su vehículo sigue siendo suyo, Fleurinelle no cobra por ello." },
        { q: "¿Cuánto puedo ganar?", a: "Depende de la zona y del número de viajes. Media 15-30 MAD por entrega. Con 5 días/semana, entre 1 500 y 2 500 MAD/mes." },
        { q: "¿Qué documentos necesito?", a: "DNI, permiso de conducir, permiso de circulación, seguro y antecedentes penales de menos de 3 meses." },
        { q: "¿Puedo repartir solo los fines de semana?", a: "Sí. Elige sus horarios y zonas, con un clic desde su cuenta." },
        { q: "¿Cómo se calcula el pago?", a: "Cada zona tiene tarifa fija visible antes de aceptar. El pago completo llega el lunes a su RIB, con recibo PDF." },
      ],
    },
    final: { title: "¿Listo para repartir con Fleurinelle?", body: "Menos de 5 minutos. Respondemos en 24 h laborables.", cta: "Iniciar mi candidatura" },
    form: {
      title: "Candidatura repartidor Fleurinelle",
      intro: "Trabajamos con repartidores independientes en Casablanca, Marrakech, Rabat, Tánger y Agadir. Pago por viaje, ingresos cada lunes.",
      latinNotice: "Rellene todos los campos en caracteres latinos (a-z, 0-9).",
      section1: "1. Sus datos",
      name: "Nombre completo", email: "Correo", password: "Contraseña (8+)", phone: "Teléfono", whatsapp: "WhatsApp",
      cin: "N° de DNI (CIN)", licenseNumber: "N° permiso de conducir",
      section2: "2. Su vehículo",
      vehicleType: "Tipo de vehículo",
      typeScooter: "Scooter", typeCar: "Coche", typeVan: "Furgoneta",
      brand: "Marca", model: "Modelo", year: "Año", plate: "Matrícula",
      section3: "3. ¿Dónde quiere repartir?",
      city: "Ciudad", zones: "Zonas", zonesHint: "Seleccione todas las zonas que desea cubrir.",
      section4: "4. Documentos obligatorios",
      docsHint: "Suba cada documento en JPG o PDF (máx. 8 MB). Nuestro equipo los revisa en 24 h laborables.",
      docsAccept: "JPG o PDF · máx. 8 MB",
      docJpgOnly: "JPG · máx. 8 MB",
      docUpload: "Subir", docChange: "Reemplazar",
      docRecto: "Frontal", docVerso: "Trasera",
      docErrSize: "Archivo demasiado grande (máx. 8 MB).",
      docErrFormat: "Formato no admitido. Suba un JPG o un PDF.",
      docErrJpg: "Este documento debe ser una foto JPG.",
      criminal: "Antecedentes penales", criminalHint: "PDF o foto. Menos de 3 meses.",
      idDoc: "DNI (frontal o ambas caras)", licenseDoc: "Permiso de conducir",
      autoAttestation: "Certificado de autónomo",
      autoCard: "Tarjeta de autónomo",
      carteGrise: "Permiso de circulación",
      insurance: "Certificado de seguro",
      vehicleBack: "Foto de la matrícula trasera del vehículo",
      docPdfOrJpg: "PDF, JPG o JPEG.",
      docOptional: "Opcional — súbalo más tarde desde la app.",
      submit: "Enviar candidatura", submitting: "Enviando…",
      consent: "He leído y acepto el", agreement: "Contrato de repartidor asociado", consentEnd: ", en mi propio nombre como autónomo independiente.",
      chooseCity: "— Elegir —",
      submitHint: "Menos de 5 minutos. Respondemos en 24 h laborables.",
      success: "Candidatura enviada",
      successHint: "Nuestro equipo revisa su expediente. Recibirá un correo al activarse.",
      errPhone: "Número demasiado corto.", errEmail: "Formato de correo no válido.",
      errPassword: "caracteres mínimo.", errName: "Nombre demasiado corto.",
      errPlate: "Matrícula no válida.", errYear: "Año no válido.",
      brandPh: "p. ej. Honda", modelPh: "p. ej. PCX 125", yearPh: "p. ej. 2019", platePh: "12345-A-6",
      cinPh: "AB123456", licenseNumberPh: "M/123456",
    },
  },
  ar: {
    dir: "rtl",
    hero: {
      eyebrow: "سائقو Fleurinelle · المغرب",
      headA: "توصيلاتكم تستحقّ", headB: "دخلاً حقيقياً.",
      body: "انضمّوا إلى شبكة سائقي Fleurinelle. اختاروا مناطقكم وأيّامكم. الأجر ثابت لكلّ توصيلة، بالدرهم، وكلّ يوم إثنين.",
      cta: "بدء طلبي", loginLink: "أنا سائق بالفعل",
      badgeFree: "بدون رسوم اشتراك", badgeFlex: "أوقاتكم ومناطقكم", badgeFast: "تفعيل خلال 24 ساعة",
      floatingKicker: "هذا الشهر", floatingSub: "متوسّط الدخل\nللسائقين النشطين 5 أيام",
    },
    stats: [
      { value: "20 درهم", label: "متوسّط الأجر لكلّ توصيلة ناجحة" },
      { value: "الإثنين", label: "تسديد أسبوعي إلى RIB الخاص بكم" },
      { value: "24 ساعة", label: "مدّة التفعيل بعد المراجعة" },
      { value: "0 درهم", label: "بدون أيّة رسوم اشتراك أبداً" },
    ],
    benefits: {
      eyebrow: "لماذا القيادة لصالح Fleurinelle",
      title: "أجر شفّاف،", titleB: "وانتظار أقلّ فعلاً.",
      cards: [
        { icon: Wallet, title: "أجر حسب المنطقة", body: "لكلّ منطقة تعرفة ثابتة معروضة. تعرفون مسبقاً كم تجني الجولة قبل قبولها." },
        { icon: Clock, title: "تسديد أسبوعي", body: "تحويل تلقائي كلّ إثنين إلى RIB بالدرهم. كشف PDF مفصّل كلّ أسبوع." },
        { icon: MapPin, title: "أنتم تختارون مناطقكم", body: "اختاروا فقط المناطق التي تريدون تغطيتها. يمكنكم تغييرها في أيّ وقت من التطبيق." },
        { icon: ShieldCheck, title: "التأمين مشمول", body: "تُؤمّن Fleurinelle كلّ باقة أثناء النقل، مع دعم 24 / 7 عند الطوارئ." },
        { icon: ThumbsUp, title: "تطبيق بسيط", body: "شاشة واحدة: الاستلام، عنوان الزبون، خريطة GPS، زرّ صورة الإثبات. لا موزّع تتّصلون به." },
        { icon: Bike, title: "مركبتكم، قواعدكم", body: "سكوتر، سيّارة أو فرغونيت: نقبل كلّ مركبة مؤمَّنة وقانونية." },
      ],
    },
    how: {
      eyebrow: "كيف تعمل الخدمة", title: "التفعيل خلال أسبوع.",
      steps: [
        { t: "طلب في 5 دقائق", d: "استمارة واحدة — بياناتكم، مركبتكم، بضع وثائق (البطاقة الوطنية، الرخصة، السوابق العدلية)." },
        { t: "المراجعة خلال 24 ساعة", d: "يُدقّق فريقنا الوثائق والمناطق. تتابعون كلّ مرحلة من تطبيق السائق." },
        { t: "توصيلة تجريبية ثمّ الانطلاق", d: "توصيلة تجريبية تؤكّد سيرورتكم. أوّل التوصيلات المدفوعة تصلكم في نفس الأسبوع." },
      ],
    },
    insight: {
      eyebrow: "دخل إضافي حقيقي",
      titleA: "وصّلوا عندما يناسبكم،", titleB: "اربحوا كلّ أسبوع.",
      body: "تُنشئ Fleurinelle طلبات طوال الأسبوع — ذروة الجمعة والسبت والأحد وكلّ عيد. السائقون النشطون 5 أيّام يتجاوزون 2 000 درهم شهرياً.",
      points: [
        ["+22 %", "طلبات إضافية يوم السبت مقارنةً بأيّام العمل العادية."],
        ["18 دقيقة", "متوسّط الوقت بين قبول الجولة وتسليمها للزبون."],
        ["93 %", "من الجولات بدون أيّة حوادث مصرّح بها."],
        ["3 ذُرا", "من النشاط الكثيف سنوياً (فالنتاين، عيد الأمّهات، العيد)."],
      ],
      panelKicker: "أين نوصّل",
      panelTitle: "الدار البيضاء ومراكش والرباط وطنجة وأكادير — ونفتح مدناً جديدة كلّ ثلاثة أشهر.",
      panelBody: "لا نفتح مدينة إلاّ حين تنضمّ إليها عدّة محلّات ورود — الطلب موجود منذ أوّل يوم لكم.",
    },
    testimonials: {
      eyebrow: "ماذا يقول السائقون", title: "سائقون مثلكم.",
      items: [
        { quote: "أقوم بـ 30 توصيلة أسبوعياً بالسكوتر. كلّ إثنين يصلني التحويل بالضبط كما وُعِد.", name: "يوسف ه.", role: "سكوتر · الدار البيضاء", initials: "YE" },
        { quote: "التطبيق بسيط، أرى العنوان، ألتقط الصورة، وانتهى. لا وقت ضائع مع الموزّع.", name: "كريم ت.", role: "سيّارة · مراكش", initials: "KT" },
      ],
    },
    faq: {
      eyebrow: "أسئلة متكرّرة", title: "نُجيب قبل أن تسألوا.",
      contactPrefix: "لديكم سؤال؟ راسلونا على",
      items: [
        { q: "أيّ مركبات تقبلون؟", a: "سكوتر، سيّارة أو فرغونيت — طالما مؤمَّنة وقانونية ونظيفة. مركبتكم تبقى ملككم، Fleurinelle لا تأخذ شيئاً منها." },
        { q: "كم يمكنني أن أربح؟", a: "يعتمد على المنطقة وعدد الجولات. المتوسّط 15 إلى 30 درهم لكلّ توصيلة. السائقون النشطون 5 أيّام أسبوعياً بين 1 500 و2 500 درهم شهرياً." },
        { q: "ما الوثائق المطلوبة؟", a: "البطاقة الوطنية، رخصة السياقة، رخصة السير، شهادة التأمين، ومستخرج السوابق العدلية أحدث من 3 أشهر." },
        { q: "هل يمكن التوصيل عطلة نهاية الأسبوع فقط؟", a: "نعم. تختارون أوقاتكم ومناطقكم بنقرة واحدة من فضائكم." },
        { q: "كيف يُحسب الأجر؟", a: "لكلّ منطقة تعرفة ثابتة معروضة قبل قبول الجولة. يصلكم المبلغ كاملاً يوم الإثنين إلى RIB، مع كشف PDF." },
      ],
    },
    final: { title: "مستعدّون للقيادة لصالح Fleurinelle؟", body: "أقلّ من 5 دقائق. نردّ خلال 24 ساعة عمل.", cta: "بدء طلبي" },
    form: {
      title: "طلب سائق Fleurinelle",
      intro: "نتعاون مع سائقين مستقلّين في الدار البيضاء ومراكش والرباط وطنجة وأكادير. أجر لكلّ جولة، تسديد كلّ إثنين.",
      latinNotice: "يجب إدخال جميع الحقول بالأحرف اللاتينية (a-z، 0-9).",
      section1: "1. بياناتكم",
      name: "الاسم الكامل", email: "البريد", password: "كلمة السرّ (8+)", phone: "الهاتف", whatsapp: "واتساب",
      cin: "رقم البطاقة الوطنية", licenseNumber: "رقم رخصة السياقة",
      section2: "2. مركبتكم",
      vehicleType: "نوع المركبة",
      typeScooter: "سكوتر", typeCar: "سيّارة", typeVan: "فرغونيت",
      brand: "الماركة", model: "الموديل", year: "السنة", plate: "لوحة التسجيل",
      section3: "3. أين تريدون التوصيل؟",
      city: "المدينة", zones: "المناطق", zonesHint: "اختاروا كلّ المناطق التي تودّون تغطيتها.",
      section4: "4. الوثائق الإلزامية",
      docsHint: "حمّلوا كلّ وثيقة بصيغة JPG أو PDF (8 ميغا كحدّ أقصى). يُراجعها فريقنا خلال 24 ساعة عمل.",
      docsAccept: "JPG أو PDF · 8 ميغا كحدّ أقصى",
      docJpgOnly: "JPG · 8 ميغا كحدّ أقصى",
      docUpload: "تحميل", docChange: "استبدال",
      docRecto: "الوجه", docVerso: "الظهر",
      docErrSize: "الملفّ كبير جداً (8 ميغا كحدّ أقصى).",
      docErrFormat: "الصيغة غير مقبولة. حمّلوا JPG أو PDF.",
      docErrJpg: "يجب أن تكون هذه الوثيقة بصيغة JPG.",
      criminal: "مستخرج السوابق العدلية", criminalHint: "PDF أو صورة. أحدث من 3 أشهر.",
      idDoc: "البطاقة الوطنية (وجه أو الوجهان)", licenseDoc: "رخصة السياقة",
      autoAttestation: "شهادة المقاول الذاتي",
      autoCard: "بطاقة المقاول الذاتي",
      carteGrise: "رخصة السير",
      insurance: "شهادة التأمين",
      vehicleBack: "صورة اللوحة الخلفية للمركبة",
      docPdfOrJpg: "PDF أو JPG أو JPEG.",
      docOptional: "اختياري — يمكنكم تحميله لاحقاً من التطبيق.",
      submit: "إرسال الطلب", submitting: "جارٍ الإرسال…",
      consent: "قرأتُ ووافقتُ على", agreement: "اتفاقية سائق التوصيل الشريك", consentEnd: "، باسمي كمقاول ذاتي مستقلّ.",
      chooseCity: "— اختر —",
      submitHint: "أقلّ من 5 دقائق. نردّ خلال 24 ساعة عمل.",
      success: "تمّ إرسال الطلب",
      successHint: "يراجع فريقنا ملفّكم. ستصلكم رسالة عند التفعيل.",
      errPhone: "الرقم قصير جداً.", errEmail: "صيغة البريد غير صالحة.",
      errPassword: "أحرف على الأقلّ.", errName: "الاسم قصير جداً.",
      errPlate: "لوحة تسجيل غير صالحة.", errYear: "سنة غير صالحة.",
      brandPh: "مثلاً Honda", modelPh: "مثلاً PCX 125", yearPh: "مثلاً 2019", platePh: "12345-A-6",
      cinPh: "AB123456", licenseNumberPh: "M/123456",
    },
  },
};

const scrollToForm = () => {
  const el = document.getElementById("driver-apply-form");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = ({ c }) => (
  <section className="relative overflow-hidden bg-ink text-plaster" data-testid="driver-hero">
    <div className="absolute inset-0 opacity-[0.08]" aria-hidden="true"
         style={{ backgroundImage: "radial-gradient(circle at 15% 15%, #C9A96B 0px, transparent 40%), radial-gradient(circle at 85% 85%, #C9A96B 0px, transparent 45%)" }} />
    <div className="max-w-content mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
      <div className="space-y-7 animate-fadeUp">
        <p className="smallcaps text-brass tracking-[0.25em]">{c.hero.eyebrow}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          {c.hero.headA}<br /><span className="italic text-brass-light">{c.hero.headB}</span>
        </h1>
        <p className="text-lg text-plaster/80 leading-relaxed max-w-lg">{c.hero.body}</p>
        <div className="flex flex-wrap items-center gap-4 pt-2">
          <button onClick={scrollToForm} data-testid="driver-hero-cta"
                  className="group inline-flex items-center gap-2 bg-brass text-ink px-7 py-3.5 font-medium text-sm tracking-wide hover:bg-brass-light transition-colors">
            {c.hero.cta}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </button>
          <a href="/driver" className="text-sm text-plaster/70 hover:text-plaster underline underline-offset-4 decoration-brass/50">{c.hero.loginLink}</a>
        </div>
        <div className="flex flex-wrap items-center gap-6 pt-6 text-xs text-plaster/60">
          <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-brass" /> {c.hero.badgeFree}</span>
          <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-brass" /> {c.hero.badgeFlex}</span>
          <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-brass" /> {c.hero.badgeFast}</span>
        </div>
      </div>
      <div className="relative animate-fadeUp" style={{ animationDelay: "120ms" }}>
        <div className="aspect-[4/5] overflow-hidden border border-brass/30">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" loading="eager" />
        </div>
        <div className="absolute -bottom-6 -left-6 hidden sm:block bg-plaster text-ink p-4 border border-brass-light shadow-xl">
          <p className="smallcaps text-brass text-[10px]">{c.hero.floatingKicker}</p>
          <p className="font-display text-2xl mt-1">2 100 MAD</p>
          <p className="text-[11px] text-muted leading-tight whitespace-pre-line">{c.hero.floatingSub}</p>
        </div>
      </div>
    </div>
  </section>
);

const StatsBar = ({ c }) => (
  <section className="bg-plaster-deep border-y border-brass-light" data-testid="driver-stats">
    <div className="max-w-content mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
      {c.stats.map((s, i) => (
        <div key={i} className="text-left border-l-2 border-brass ps-4 rtl:border-l-0 rtl:border-r-2 rtl:ps-0 rtl:pe-4">
          <p className="font-display text-3xl lg:text-4xl text-ink tabular">{s.value}</p>
          <p className="text-xs text-muted mt-2 leading-snug">{s.label}</p>
        </div>
      ))}
    </div>
  </section>
);

const Benefits = ({ c }) => (
  <section className="max-w-content mx-auto px-6 lg:px-10 py-20 lg:py-24">
    <div className="max-w-2xl mb-14">
      <p className="smallcaps text-brass tracking-[0.25em]">{c.benefits.eyebrow}</p>
      <h2 className="font-display text-3xl lg:text-4xl text-ink mt-3 leading-tight">{c.benefits.title}<br />{c.benefits.titleB}</h2>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {c.benefits.cards.map((b, i) => {
        const Icon = b.icon;
        return (
          <div key={i} className="border border-brass-light bg-plaster p-6 hover:border-brass transition-colors group animate-fadeUp"
               style={{ animationDelay: `${i * 60}ms` }}>
            <div className="w-11 h-11 border border-brass flex items-center justify-center text-brass mb-5 group-hover:bg-brass group-hover:text-ink transition-colors">
              <Icon size={20} />
            </div>
            <h3 className="font-display text-xl text-ink">{b.title}</h3>
            <p className="text-sm text-muted mt-2 leading-relaxed">{b.body}</p>
          </div>
        );
      })}
    </div>
  </section>
);

const HowItWorks = ({ c }) => (
  <section className="bg-ink text-plaster py-20 lg:py-24">
    <div className="max-w-content mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr,1.1fr] gap-14 items-center">
      <div className="aspect-[5/4] overflow-hidden border border-brass/30 order-2 lg:order-1">
        <img src={CITY_IMG} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="order-1 lg:order-2">
        <p className="smallcaps text-brass tracking-[0.25em]">{c.how.eyebrow}</p>
        <h2 className="font-display text-3xl lg:text-4xl mt-3 leading-tight">{c.how.title}</h2>
        <ol className="mt-10 space-y-8">
          {c.how.steps.map((s, i) => (
            <li key={i} className="flex gap-6 animate-fadeUp" style={{ animationDelay: `${i * 100}ms` }}>
              <span className="font-display text-3xl text-brass shrink-0 leading-none tabular">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="font-display text-xl text-plaster">{s.t}</h3>
                <p className="text-sm text-plaster/70 mt-1.5 max-w-md leading-relaxed">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

const Insight = ({ c }) => (
  <section className="max-w-content mx-auto px-6 lg:px-10 py-20 lg:py-24">
    <div className="grid lg:grid-cols-[1.1fr,1fr] gap-14 items-start">
      <div>
        <p className="smallcaps text-brass tracking-[0.25em]">{c.insight.eyebrow}</p>
        <h2 className="font-display text-3xl lg:text-4xl text-ink mt-3 leading-tight">{c.insight.titleA}<br /><span className="italic">{c.insight.titleB}</span></h2>
        <p className="text-base text-charcoal mt-6 max-w-lg leading-relaxed">{c.insight.body}</p>
        <div className="mt-10 space-y-6">
          {c.insight.points.map(([v, l], i) => (
            <div key={i} className="flex gap-6 items-baseline border-b border-brass-light pb-5 last:border-0">
              <span className="font-display text-3xl text-ink tabular w-28 shrink-0">{v}</span>
              <span className="text-sm text-charcoal">{l}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-plaster-deep border border-brass-light p-8 lg:p-10 space-y-6">
        <div className="flex items-center gap-3 text-brass">
          <MapPin size={18} />
          <p className="smallcaps text-xs">{c.insight.panelKicker}</p>
        </div>
        <h3 className="font-display text-2xl text-ink leading-snug">{c.insight.panelTitle}</h3>
        <p className="text-sm text-charcoal leading-relaxed">{c.insight.panelBody}</p>
      </div>
    </div>
  </section>
);

const Testimonials = ({ c }) => (
  <section className="bg-plaster-deep py-20 lg:py-24">
    <div className="max-w-content mx-auto px-6 lg:px-10">
      <p className="smallcaps text-brass tracking-[0.25em]">{c.testimonials.eyebrow}</p>
      <h2 className="font-display text-3xl lg:text-4xl text-ink mt-3 leading-tight">{c.testimonials.title}</h2>
      <div className="grid md:grid-cols-2 gap-8 mt-12">
        {c.testimonials.items.map((t, i) => (
          <figure key={i} className="bg-plaster border border-brass-light p-8 flex flex-col gap-6">
            <div className="flex text-brass">{[0, 1, 2, 3, 4].map((s) => <Star key={s} size={14} fill="currentColor" strokeWidth={0} className="mr-0.5" />)}</div>
            <blockquote className="font-display text-xl text-ink leading-snug">« {t.quote} »</blockquote>
            <figcaption className="flex items-center gap-4 mt-auto">
              <div className="w-12 h-12 rounded-full border border-brass bg-plaster-deep flex items-center justify-center font-display text-lg text-ink">{t.initials}</div>
              <div>
                <p className="text-sm text-ink font-medium">{t.name}</p>
                <p className="text-xs text-muted">{t.role}</p>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const Faq = ({ c }) => {
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-content mx-auto px-6 lg:px-10 py-20 lg:py-24">
      <div className="grid lg:grid-cols-[1fr,2fr] gap-14">
        <div>
          <p className="smallcaps text-brass tracking-[0.25em]">{c.faq.eyebrow}</p>
          <h2 className="font-display text-3xl lg:text-4xl text-ink mt-3 leading-tight">{c.faq.title}</h2>
          <p className="text-sm text-muted mt-4 max-w-sm">{c.faq.contactPrefix}{" "}
            <a href="mailto:drivers@fleurinelle.com" className="text-brass underline underline-offset-4">drivers@fleurinelle.com</a>
          </p>
        </div>
        <div className="border-t border-brass-light">
          {c.faq.items.map((f, i) => (
            <div key={i} className="border-b border-brass-light">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full py-5 flex items-center justify-between text-left gap-6 group">
                <span className="font-display text-lg text-ink">{f.q}</span>
                <ChevronDown size={18} className={`shrink-0 text-brass transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                  <p className="text-sm text-charcoal pb-6 pe-10 leading-relaxed">{f.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCta = ({ c }) => (
  <section className="bg-ink text-plaster py-16 lg:py-20">
    <div className="max-w-content mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
      <div className="max-w-xl">
        <h2 className="font-display text-3xl lg:text-4xl leading-tight">{c.final.title}</h2>
        <p className="text-sm text-plaster/70 mt-3">{c.final.body}</p>
      </div>
      <button onClick={scrollToForm} data-testid="driver-final-cta"
              className="group inline-flex items-center gap-2 bg-brass text-ink px-8 py-4 font-medium text-sm tracking-wide hover:bg-brass-light transition-colors shrink-0">
        {c.final.cta}
        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform rtl:rotate-180 rtl:group-hover:-translate-x-1" />
      </button>
    </div>
  </section>
);

// -------- Apply form --------

const VEHICLE_ICONS = { scooter: Bike, car: Car, van: TruckIcon };
const CURRENT_YEAR = new Date().getFullYear();

const DriverApplyForm = ({ c, locale }) => {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", whatsapp: "",
    national_id: "", driving_license_number: "",
    vehicle_type: "scooter", vehicle_brand: "", vehicle_model: "", vehicle_year: "", license_plate: "",
    city_id: "", zone_ids: [], accepted: false,
  });
  const [files, setFiles] = useState({
    criminal_record: null, id_document: null, driving_license: null,
    autoentrepreneur_attestation: null, autoentrepreneur_card: null,
    carte_grise_recto: null, carte_grise_verso: null,
    insurance_recto: null, insurance_verso: null,
    vehicle_back_photo: null,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const f = c.form;

  useEffect(() => {
    api.get("/drivers/meta", { params: { locale } }).then((r) => setMeta(r.data)).catch(() => {});
  }, [locale]);

  const city = useMemo(() => meta?.cities?.find((x) => x.id === form.city_id), [meta, form.city_id]);
  const toggleZone = (id) => setForm((s) => ({ ...s, zone_ids: s.zone_ids.includes(id) ? s.zone_ids.filter((x) => x !== id) : [...s.zone_ids, id] }));

  const fieldErrors = useMemo(() => {
    const e = {};
    if (form.name && form.name.trim().length < 2) e.name = f.errName;
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = f.errEmail;
    if (form.password && form.password.length < 8) e.password = `${form.password.length}/8 ${f.errPassword}`;
    if (form.phone && (form.phone || "").replace(/\D/g, "").length < 9) e.phone = f.errPhone;
    if (form.whatsapp && (form.whatsapp || "").replace(/\D/g, "").length < 9) e.whatsapp = f.errPhone;
    if (form.vehicle_year && (Number(form.vehicle_year) < 1990 || Number(form.vehicle_year) > CURRENT_YEAR + 1)) e.vehicle_year = f.errYear;
    if (form.license_plate && form.license_plate.length < 3) e.license_plate = f.errPlate;
    return e;
  }, [form, f]);

  const errCls = (k) => (fieldErrors[k] ? "!border-danger focus:!border-danger" : "");
  const REQUIRED_DOCS = [
    "criminal_record", "autoentrepreneur_attestation", "autoentrepreneur_card",
    "carte_grise_recto", "carte_grise_verso", "insurance_recto", "insurance_verso",
    "vehicle_back_photo",
  ];
  const JPG_ONLY = ["vehicle_back_photo"];
  const allDocsPresent = REQUIRED_DOCS.every((k) => !!files[k]);
  const vehicleBackIsJpg = !files.vehicle_back_photo || /\.(jpg|jpeg)$/i.test(files.vehicle_back_photo.name || "");
  const canSubmit = form.name && form.email && form.password.length >= 8 && form.phone && form.city_id
    && form.zone_ids.length && form.vehicle_type && form.license_plate && allDocsPresent && vehicleBackIsJpg
    && form.accepted && Object.keys(fieldErrors).length === 0;

  const pickDoc = (kind, file) => {
    setError("");
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setError(f.docErrSize); return; }
    const ct = (file.type || "").toLowerCase();
    const jpgOnly = JPG_ONLY.includes(kind);
    const isJpg = ct === "image/jpeg" || ct === "image/jpg";
    const isPdf = ct === "application/pdf";
    if (jpgOnly && !isJpg) { setError(f.docErrJpg); return; }
    if (!jpgOnly && !isJpg && !isPdf) { setError(f.docErrFormat); return; }
    setFiles((d) => ({ ...d, [kind]: file }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const dial = (v) => {
        const d = (v || "").replace(/[\s-]/g, "").replace(/^\+?212/, "").replace(/^0+/, "");
        return d ? `+212${d}` : "";
      };
      const fd = new FormData();
      Object.entries({
        name: form.name, email: form.email, password: form.password,
        phone: dial(form.phone), whatsapp: dial(form.whatsapp || form.phone),
        city_id: form.city_id, zone_ids: JSON.stringify(form.zone_ids),
        vehicle_type: form.vehicle_type, vehicle_brand: form.vehicle_brand,
        vehicle_model: form.vehicle_model, vehicle_year: form.vehicle_year || "",
        license_plate: form.license_plate,
        driving_license_number: form.driving_license_number, national_id: form.national_id,
        locale,
        terms_accepted: form.accepted ? "1" : "",
      }).forEach(([k, v]) => fd.append(k, v));
      if (files.criminal_record) fd.append("criminal_record", files.criminal_record);
      if (files.id_document) fd.append("id_document", files.id_document);
      if (files.driving_license) fd.append("driving_license", files.driving_license);
      REQUIRED_DOCS.filter((k) => k !== "criminal_record").forEach((k) => {
        if (files[k]) fd.append(k, files[k]);
      });
      const { data } = await api.post("/drivers/signup", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data);
      trackPartnerSignup({ audience: "driver", applicationId: data?.application_id || "", city: form.city_id, mode: form.vehicle_type });
    } catch (err) {
      setError(errText(err));
    } finally { setBusy(false); }
  };

  if (done)
    return (
      <div id="driver-apply-form" className="max-w-xl mx-auto p-8 text-center space-y-4" data-testid="driver-apply-success">
        <CheckCircle2 className="mx-auto text-success" size={40} />
        <h1 className="font-display text-2xl text-ink">{f.success}</h1>
        <p className="text-sm text-muted">{f.successHint}</p>
      </div>
    );

  return (
    <form onSubmit={submit} id="driver-apply-form" className="max-w-3xl mx-auto p-6 space-y-6 scroll-mt-8" data-testid="driver-apply-form">
      <h1 className="font-display text-3xl text-ink">{f.title}</h1>
      <p className="text-sm text-muted leading-relaxed">{f.intro}</p>
      <p className="text-xs text-muted">{f.latinNotice}</p>

      <section className="card p-5 space-y-3">
        <h2 className="font-display text-lg text-ink">{f.section1}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.name} *</span>
            <input required data-testid="driver-name" className={`field w-full mt-1 ${errCls("name")}`}
                   value={form.name} onChange={(e) => setForm({ ...form, name: latinOnly(e.target.value) })} />
            {fieldErrors.name ? <span className="text-[11px] text-danger">{fieldErrors.name}</span> : null}
          </label>
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.email} *</span>
            <input required type="email" data-testid="driver-email" className={`field w-full mt-1 ${errCls("email")}`}
                   value={form.email} onChange={(e) => setForm({ ...form, email: latinOnly(e.target.value) })} />
            {fieldErrors.email ? <span className="text-[11px] text-danger">{fieldErrors.email}</span> : null}
          </label>
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.password} *</span>
            <input required type="password" data-testid="driver-password" className={`field w-full mt-1 ${errCls("password")}`}
                   value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            {fieldErrors.password ? <span className="text-[11px] text-danger">{fieldErrors.password}</span> : null}
          </label>
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.phone} *</span>
            <div className="flex gap-2 mt-1">
              <span className="field !w-[86px] flex items-center justify-center gap-1 text-sm shrink-0">{MOROCCO.flag} {MOROCCO.dial}</span>
              <input required data-testid="driver-phone" className={`field flex-1 ${errCls("phone")}`}
                     value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            {fieldErrors.phone ? <span className="text-[11px] text-danger">{fieldErrors.phone}</span> : null}
          </label>
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.whatsapp}</span>
            <div className="flex gap-2 mt-1">
              <span className="field !w-[86px] flex items-center justify-center gap-1 text-sm shrink-0">{MOROCCO.flag} {MOROCCO.dial}</span>
              <input data-testid="driver-whatsapp" className={`field flex-1 ${errCls("whatsapp")}`}
                     value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
            </div>
          </label>
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.cin}</span>
            <input data-testid="driver-cin" className="field w-full mt-1" placeholder={f.cinPh}
                   value={form.national_id} onChange={(e) => setForm({ ...form, national_id: latinOnly(e.target.value) })} />
          </label>
          <label className="block sm:col-span-2"><span className="text-xs text-muted uppercase tracking-wider">{f.licenseNumber}</span>
            <input data-testid="driver-license-number" className="field w-full mt-1" placeholder={f.licenseNumberPh}
                   value={form.driving_license_number} onChange={(e) => setForm({ ...form, driving_license_number: latinOnly(e.target.value) })} />
          </label>
        </div>
      </section>

      <section className="card p-5 space-y-4">
        <h2 className="font-display text-lg text-ink">{f.section2}</h2>
        <div>
          <span className="text-xs text-muted uppercase tracking-wider">{f.vehicleType} *</span>
          <div className="grid grid-cols-3 gap-3 mt-2" data-testid="driver-vehicle-type">
            {[
              ["scooter", f.typeScooter], ["car", f.typeCar], ["van", f.typeVan],
            ].map(([k, label]) => {
              const Icon = VEHICLE_ICONS[k];
              const active = form.vehicle_type === k;
              return (
                <button key={k} type="button" data-testid={`vehicle-${k}`}
                        onClick={() => setForm({ ...form, vehicle_type: k })}
                        className={`flex flex-col items-center gap-2 py-4 border transition-colors ${active ? "border-ink bg-plaster-deep text-ink" : "border-brass-light text-muted hover:border-brass"}`}>
                  <Icon size={22} />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.brand}</span>
            <input data-testid="driver-brand" className="field w-full mt-1" placeholder={f.brandPh}
                   value={form.vehicle_brand} onChange={(e) => setForm({ ...form, vehicle_brand: latinOnly(e.target.value) })} />
          </label>
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.model}</span>
            <input data-testid="driver-model" className="field w-full mt-1" placeholder={f.modelPh}
                   value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: latinOnly(e.target.value) })} />
          </label>
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.year}</span>
            <input data-testid="driver-year" type="number" min="1990" max={CURRENT_YEAR + 1} className={`field w-full mt-1 ${errCls("vehicle_year")}`}
                   placeholder={f.yearPh}
                   value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
            {fieldErrors.vehicle_year ? <span className="text-[11px] text-danger">{fieldErrors.vehicle_year}</span> : null}
          </label>
          <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.plate} *</span>
            <input required data-testid="driver-plate" className={`field w-full mt-1 tabular ${errCls("license_plate")}`}
                   placeholder={f.platePh} maxLength={20}
                   value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: latinOnly(e.target.value).toUpperCase() })} />
            {fieldErrors.license_plate ? <span className="text-[11px] text-danger">{fieldErrors.license_plate}</span> : null}
          </label>
        </div>
      </section>

      <section className="card p-5 space-y-3">
        <h2 className="font-display text-lg text-ink">{f.section3}</h2>
        <label className="block"><span className="text-xs text-muted uppercase tracking-wider">{f.city} *</span>
          <select required data-testid="driver-city" className="field w-full mt-1"
                  value={form.city_id} onChange={(e) => setForm({ ...form, city_id: e.target.value, zone_ids: [] })}>
            <option value="">{f.chooseCity}</option>
            {meta?.cities?.map((c2) => <option key={c2.id} value={c2.id}>{c2.name}</option>)}
          </select>
        </label>
        {city ? (
          <div>
            <span className="text-xs text-muted uppercase tracking-wider">{f.zones} *</span>
            <p className="text-[11px] text-muted mt-1">{f.zonesHint}</p>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="driver-zones">
              {city.zones.map((z) => (
                <button key={z.id} type="button" data-testid={`driver-zone-${z.id}`} onClick={() => toggleZone(z.id)}
                        className={`text-xs px-3 py-1.5 border ${form.zone_ids.includes(z.id) ? "border-ink bg-plaster-deep text-ink" : "border-brass-light text-muted"}`}>
                  {z.name}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <section className="card p-5 space-y-3" data-testid="driver-docs-section">
        <h2 className="font-display text-lg text-ink">{f.section4}</h2>
        <p className="text-xs text-muted leading-relaxed">{f.docsHint}</p>

        {/* Single-file mandatory rows — ordered most → least critical */}
        {[
          ["criminal_record", f.criminal],
          ["autoentrepreneur_attestation", f.autoAttestation],
          ["autoentrepreneur_card", f.autoCard],
        ].map(([kind, label]) => (
          <div key={kind} className="flex flex-wrap items-center gap-3 border border-brass-light p-3"
               data-testid={`driver-doc-row-${kind}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="text-sm text-ink">{label} <span className="text-danger">*</span></p>
              {files[kind] ? (
                <p className="text-[11px] text-success flex items-center gap-1"><CheckCircle2 size={12} /> {files[kind].name}</p>
              ) : (
                <p className="text-[11px] text-muted">{f.docsAccept}</p>
              )}
            </div>
            <label className="btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-2">
              <Upload size={13} />
              {files[kind] ? f.docChange : f.docUpload}
              <input type="file" accept="image/jpeg,application/pdf" className="hidden"
                     data-testid={`driver-doc-${kind}`}
                     onChange={(e) => pickDoc(kind, e.target.files[0])} />
            </label>
          </div>
        ))}

        {/* Recto/Verso pairs — two upload buttons on one line */}
        {[
          ["carte_grise", f.carteGrise, ["carte_grise_recto", "carte_grise_verso"]],
          ["insurance", f.insurance, ["insurance_recto", "insurance_verso"]],
        ].map(([groupKey, label, [rectoKey, versoKey]]) => (
          <div key={groupKey} className="flex flex-wrap items-center gap-3 border border-brass-light p-3"
               data-testid={`driver-doc-row-${groupKey}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="text-sm text-ink">{label} <span className="text-danger">*</span></p>
              {files[rectoKey] || files[versoKey] ? (
                <p className="text-[11px] text-success flex items-center gap-1"><CheckCircle2 size={12} />
                  {[files[rectoKey] && f.docRecto, files[versoKey] && f.docVerso].filter(Boolean).join(" · ")}
                </p>
              ) : (
                <p className="text-[11px] text-muted">{f.docsAccept}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className={`btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-1 ${files[rectoKey] ? "border-success text-success" : ""}`}>
                {files[rectoKey] ? <CheckCircle2 size={13} /> : <Upload size={13} />}
                {f.docRecto}
                <input type="file" accept="image/jpeg,application/pdf" className="hidden"
                       data-testid={`driver-doc-${rectoKey}`}
                       onChange={(e) => pickDoc(rectoKey, e.target.files[0])} />
              </label>
              <label className={`btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-1 ${files[versoKey] ? "border-success text-success" : ""}`}>
                {files[versoKey] ? <CheckCircle2 size={13} /> : <Upload size={13} />}
                {f.docVerso}
                <input type="file" accept="image/jpeg,application/pdf" className="hidden"
                       data-testid={`driver-doc-${versoKey}`}
                       onChange={(e) => pickDoc(versoKey, e.target.files[0])} />
              </label>
            </div>
          </div>
        ))}

        {/* Vehicle-back plate photo — JPG only */}
        <div className="flex flex-wrap items-center gap-3 border border-brass-light p-3"
             data-testid="driver-doc-row-vehicle_back_photo">
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm text-ink">{f.vehicleBack} <span className="text-danger">*</span></p>
            {files.vehicle_back_photo ? (
              <p className="text-[11px] text-success flex items-center gap-1"><CheckCircle2 size={12} /> {files.vehicle_back_photo.name}</p>
            ) : (
              <p className="text-[11px] text-muted">{f.docJpgOnly}</p>
            )}
          </div>
          <label className="btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-2">
            <Upload size={13} />
            {files.vehicle_back_photo ? f.docChange : f.docUpload}
            <input type="file" accept="image/jpeg" className="hidden"
                   data-testid="driver-doc-vehicle_back_photo"
                   onChange={(e) => pickDoc("vehicle_back_photo", e.target.files[0])} />
          </label>
        </div>

        {/* Optional docs — CIN, driving licence — same style, no asterisk */}
        {[
          ["id_document", f.idDoc],
          ["driving_license", f.licenseDoc],
        ].map(([kind, label]) => (
          <div key={kind} className="flex flex-wrap items-center gap-3 border border-brass-light/60 p-3"
               data-testid={`driver-doc-row-${kind}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="text-sm text-ink">{label}</p>
              {files[kind] ? (
                <p className="text-[11px] text-success flex items-center gap-1"><CheckCircle2 size={12} /> {files[kind].name}</p>
              ) : (
                <p className="text-[11px] text-muted">{f.docOptional}</p>
              )}
            </div>
            <label className="btn-ghost text-xs py-2 px-3 cursor-pointer inline-flex items-center gap-2">
              <Upload size={13} />
              {files[kind] ? f.docChange : f.docUpload}
              <input type="file" accept="image/jpeg,application/pdf" className="hidden"
                     data-testid={`driver-doc-${kind}`}
                     onChange={(e) => pickDoc(kind, e.target.files[0])} />
            </label>
          </div>
        ))}
      </section>

      <label className="flex items-start gap-3 text-xs text-charcoal cursor-pointer card p-4"
             data-testid="driver-consent-row">
        <input required type="checkbox" data-testid="driver-consent" className="mt-0.5 shrink-0"
               checked={form.accepted}
               onChange={(e) => setForm({ ...form, accepted: e.target.checked })} />
        <span>
          {f.consent}{" "}
          <a href={`/${locale}/driver-agreement`} target="_blank" rel="noreferrer"
             className="text-brass underline underline-offset-4"
             data-testid="driver-agreement-link">{f.agreement}</a>{f.consentEnd}
        </span>
      </label>

      {error ? <p className="text-danger text-sm card p-3" data-testid="driver-apply-error">{error}</p> : null}
      <button data-testid="driver-submit" className="btn-primary w-full inline-flex items-center justify-center gap-2" disabled={!canSubmit || busy}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : null}
        {busy ? f.submitting : f.submit}
      </button>
      <p className="text-[11px] text-muted text-center">{f.submitHint}</p>
    </form>
  );
};

export default function DriverLandingPage() {
  const { locale = "en" } = useParams();
  const lang = COPY[locale] ? locale : "en";
  const c = COPY[lang];
  const meta = SEO_META[lang];
  useEffect(() => {
    let el = document.head.querySelector('meta[name="keywords"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "keywords");
      document.head.appendChild(el);
    }
    el.setAttribute("content", meta.keywords);
  }, [meta.keywords]);
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const jobJsonLd = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: c.hero.headA + " " + c.hero.headB,
    description: meta.description,
    hiringOrganization: { "@type": "Organization", name: "Fleurinelle" },
    jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressCountry: "MA" } },
    employmentType: "CONTRACTOR",
    baseSalary: { "@type": "MonetaryAmount", currency: "MAD",
                  value: { "@type": "QuantitativeValue", value: 20, unitText: "PER_DELIVERY" } },
    datePosted: new Date().toISOString().slice(0, 10),
  };
  return (
    <div className="bg-plaster relative" dir={c.dir} data-testid="driver-landing">
      <Seo title={meta.title} description={meta.description} path={`/${lang}/driver/apply`} image={HERO_IMG} />
      <JsonLd data={faqJsonLd} />
      <JsonLd data={jobJsonLd} />
      <Hero c={c} />
      <StatsBar c={c} />
      <Benefits c={c} />
      <HowItWorks c={c} />
      <Insight c={c} />
      <Testimonials c={c} />
      <Faq c={c} />
      <FinalCta c={c} />
      <DriverApplyForm c={c} locale={lang} />
    </div>
  );
}
